import React, { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Home, Building2, Shield, Info } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';
import { 
  getAllCommunities, 
  getModelsByCommunity, 
  lookupInsurance,
  getCommunityStats
} from '../utils/coventryInsuranceLookup';

interface CoventryInsuranceSelectorProps {
  selectedCommunity: string;
  selectedModel: string;
  onCommunityChange: (community: string) => void;
  onModelChange: (model: string) => void;
  onInsuranceSelect?: (annualPremium: number) => void;
  className?: string;
}

const CoventryInsuranceSelector: React.FC<CoventryInsuranceSelectorProps> = ({
  selectedCommunity,
  selectedModel,
  onCommunityChange,
  onModelChange,
  onInsuranceSelect,
  className = ''
}) => {
  const communities = useMemo(() => getAllCommunities(), []);
  const models = useMemo(() => 
    selectedCommunity ? getModelsByCommunity(selectedCommunity) : [],
    [selectedCommunity]
  );
  
  const communityStats = useMemo(() => 
    selectedCommunity ? getCommunityStats(selectedCommunity) : null,
    [selectedCommunity]
  );
  
  const lookupResult = useMemo(() => 
    lookupInsurance(selectedCommunity, selectedModel),
    [selectedCommunity, selectedModel]
  );

  const handleCommunityChange = (value: string) => {
    onCommunityChange(value);
    onModelChange(''); // Reset model when community changes
  };

  const handleModelChange = (value: string) => {
    onModelChange(value);
    
    // Auto-apply insurance when model is selected
    const result = lookupInsurance(selectedCommunity, value);
    if (result.source === 'exact' && onInsuranceSelect) {
      onInsuranceSelect(result.annualPremium);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <Shield className="w-5 h-5 text-primary" />
        <h4 className="font-semibold">Pine Ridge Insurance Rates</h4>
        <Badge variant="outline" className="text-xs">Coventry Homes</Badge>
      </div>

      {/* Community Selector */}
      <div className="space-y-2">
        <Label htmlFor="community-select" className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-muted-foreground" />
          Community
        </Label>
        <Select value={selectedCommunity} onValueChange={handleCommunityChange}>
          <SelectTrigger id="community-select">
            <SelectValue placeholder="Select a community" />
          </SelectTrigger>
          <SelectContent>
            {communities.map(community => (
              <SelectItem key={community} value={community}>
                {community}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Community Stats */}
        {communityStats && (
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>{communityStats.modelCount} floor plans</span>
            <span>
              {formatCurrency(communityStats.minPremium)} - {formatCurrency(communityStats.maxPremium)}/yr
            </span>
          </div>
        )}
      </div>

      {/* Model Selector */}
      {selectedCommunity && (
        <div className="space-y-2">
          <Label htmlFor="model-select" className="flex items-center gap-2">
            <Home className="w-4 h-4 text-muted-foreground" />
            Floor Plan
          </Label>
          <Select value={selectedModel} onValueChange={handleModelChange}>
            <SelectTrigger id="model-select">
              <SelectValue placeholder="Select a floor plan" />
            </SelectTrigger>
            <SelectContent>
              {models.map(model => (
                <SelectItem key={model.model} value={model.model}>
                  <div className="flex items-center justify-between w-full gap-4">
                    <span>{model.model}</span>
                    <span className="text-muted-foreground text-xs">
                      {model.sqft.toLocaleString()} sqft • {formatCurrency(model.annualPremium)}/yr
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Selected Insurance Display */}
      {lookupResult.source === 'exact' && (
        <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Annual Premium</span>
            <span className="text-lg font-bold text-primary">
              {formatCurrency(lookupResult.annualPremium)}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Monthly</span>
            <span>{formatCurrency(lookupResult.monthlyPremium)}/mo</span>
          </div>
          {lookupResult.sqft && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Info className="w-3 h-3" />
              <span>
                Rate: ${(lookupResult.annualPremium / lookupResult.sqft).toFixed(2)}/sqft
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CoventryInsuranceSelector;
